/**
 * Manual test cycles + execution player.
 *
 * Manual cases (`manual_test_cases`) are the library. A cycle is a
 * release-shaped run of those cases. Each execution snapshots the
 * case at start so later library edits do not rewrite in-flight
 * results. Step results carry Pass/Fail/Blocked/Skipped plus
 * clipboard/file attachments (data-URI, size-capped).
 */

import { z } from 'zod';
import { ManualStepSchema } from './test-authoring';
import type { PmAdapterId } from './integration-pm';

export const ManualResultStatusSchema = z.enum(['untested', 'passed', 'failed', 'blocked', 'skipped']);
export type ManualResultStatus = z.infer<typeof ManualResultStatusSchema>;

export const ManualCycleStatusSchema = z.enum(['in_progress', 'completed', 'cancelled']);
export type ManualCycleStatus = z.infer<typeof ManualCycleStatusSchema>;

export const ManualAttachmentSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  contentType: z.string().min(1).max(120),
  /** data: URL. Capped server-side (see MAX_MANUAL_ATTACHMENT_BYTES). */
  dataUrl: z.string().min(1),
  bytes: z.number().int().nonnegative(),
});
export type ManualAttachment = z.infer<typeof ManualAttachmentSchema>;

export const ManualStepResultSchema = z.object({
  index: z.number().int().nonnegative(),
  status: ManualResultStatusSchema,
  actualResult: z.string().default(''),
  attachments: z.array(ManualAttachmentSchema).default([]),
});
export type ManualStepResult = z.infer<typeof ManualStepResultSchema>;

export const ManualExecutionSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  cycleId: z.string().uuid(),
  caseId: z.string().uuid(),
  caseTitle: z.string(),
  preconditions: z.array(z.string()),
  steps: z.array(ManualStepSchema),
  expectedResults: z.array(z.string()),
  stepResults: z.array(ManualStepResultSchema),
  status: ManualResultStatusSchema,
  actualResult: z.string(),
  assigneeUserId: z.string().uuid().nullable(),
  bugDraftId: z.string().uuid().nullable(),
  bugProvider: z.string().nullable(),
  externalIssueId: z.string().nullable(),
  externalIssueUrl: z.string().nullable(),
  sortIndex: z.number().int(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ManualExecution = z.infer<typeof ManualExecutionSchema>;

export const ManualCycleCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  untested: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
});
export type ManualCycleCounts = z.infer<typeof ManualCycleCountsSchema>;

export const ManualCycleSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  environment: z.string().nullable(),
  status: ManualCycleStatusSchema,
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  counts: ManualCycleCountsSchema,
});
export type ManualCycle = z.infer<typeof ManualCycleSchema>;

export const CreateManualCycleRequestSchema = z.object({
  name: z.string().min(1).max(200),
  environment: z.string().max(80).optional(),
  /** When omitted, every non-deprecated case in the project is included. */
  caseIds: z.array(z.string().uuid()).max(500).optional(),
});
export type CreateManualCycleRequest = z.infer<typeof CreateManualCycleRequestSchema>;

export const ListManualCyclesResponseSchema = z.object({
  cycles: z.array(ManualCycleSchema),
});
export type ListManualCyclesResponse = z.infer<typeof ListManualCyclesResponseSchema>;

export const GetManualCycleResponseSchema = z.object({
  cycle: ManualCycleSchema,
  executions: z.array(ManualExecutionSchema),
});
export type GetManualCycleResponse = z.infer<typeof GetManualCycleResponseSchema>;

export const RecordManualResultRequestSchema = z.object({
  status: ManualResultStatusSchema.exclude(['untested']),
  actualResult: z.string().max(8_000).optional(),
  /** When set, only this step is updated; case status is derived. */
  stepIndex: z.number().int().nonnegative().optional(),
});
export type RecordManualResultRequest = z.infer<typeof RecordManualResultRequestSchema>;

export const AddManualAttachmentRequestSchema = z.object({
  stepIndex: z.number().int().nonnegative(),
  name: z.string().min(1).max(200),
  contentType: z.string().min(1).max(120),
  dataUrl: z.string().min(1).max(2_500_000),
});
export type AddManualAttachmentRequest = z.infer<typeof AddManualAttachmentRequestSchema>;

export const FileManualBugRequestSchema = z.object({
  provider: z.string().min(1).max(40),
  stepIndex: z.number().int().nonnegative().optional(),
});
export type FileManualBugRequest = z.infer<typeof FileManualBugRequestSchema>;

export const FileManualBugResponseSchema = z.object({
  execution: ManualExecutionSchema,
  draftId: z.string().uuid(),
  provider: z.string(),
});
export type FileManualBugResponse = z.infer<typeof FileManualBugResponseSchema>;

export const CreateManualTestRequestSchema = z.object({
  title: z.string().min(1).max(300),
  caseType: z.enum(['happy', 'edge', 'negative', 'security', 'perf', 'accessibility']).default('happy'),
  priority: z.enum(['low', 'medium', 'high', 'critical']).default('medium'),
  preconditions: z.array(z.string()).default([]),
  steps: z.array(ManualStepSchema).default([{ action: 'Perform the steps' }]),
  expectedResults: z.array(z.string()).default(['The expected outcome occurs']),
  tags: z.array(z.string()).default([]),
});
export type CreateManualTestRequest = z.infer<typeof CreateManualTestRequestSchema>;

/** Soft cap for a single pasted screenshot / file (bytes of the data URL). */
export const MAX_MANUAL_ATTACHMENT_BYTES = 1_500_000;

export type ManualBugProvider = PmAdapterId;
